import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { anunciosService, uploadService, authService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import './CriarAnuncio.css'

function CriarAnuncio() {
    const navigate = useNavigate()
    const { usuario, isLogado, carregando, atualizarUsuario } = useAuth()
    const [submitting, setSubmitting] = useState(false)
    const [erro, setErro] = useState('')
    const [uploading, setUploading] = useState(false)

    const [form, setForm] = useState({
        titulo: '',
        descricao: '',
        preco: '',
        quantidade: '',
        peso: '',
        unidadePeso: 'kg', // Novo
        idade: '',
        unidadeIdade: 'meses',
        raca: 'Nelore',
        genero: 'Macho',
        categoria: 'Bovino',
        nomeFazenda: '',
        cidade: '',
        estado: '',
        imagens: ['', '', '']
    })

    // Raças por categoria
    const racasPorCategoria = {
        Bovino: ['Nelore', 'Angus', 'Brahman', 'Brangus', 'Senepol', 'Girolando', 'Holandês', 'Jersey', 'Outra'],
        Equino: ['Crioulo', 'Quarto de Milha', 'Mangalarga', 'Campolina', 'Árabe', 'Outra'],
        Suíno: ['Landrace', 'Large White', 'Duroc', 'Pietrain', 'Outra'],
        Ovino: ['Dorper', 'Santa Inês', 'Texel', 'Ille de France', 'Outra'],
        Caprino: ['Boer', 'Saanen', 'Anglo-Nubiana', 'Outra'],
        Aves: ['Galinha Caipira', 'Frango de Corte', 'Poedeira', 'Índio Gigante', 'Outra'],
        Outros: ['Outra']
    }

    // Estados com nome completo
    const estados = [
        { sigla: 'AC', nome: 'Acre' }, { sigla: 'AL', nome: 'Alagoas' }, { sigla: 'AP', nome: 'Amapá' },
        { sigla: 'AM', nome: 'Amazonas' }, { sigla: 'BA', nome: 'Bahia' }, { sigla: 'CE', nome: 'Ceará' },
        { sigla: 'DF', nome: 'Distrito Federal' }, { sigla: 'ES', nome: 'Espírito Santo' }, { sigla: 'GO', nome: 'Goiás' },
        { sigla: 'MA', nome: 'Maranhão' }, { sigla: 'MT', nome: 'Mato Grosso' }, { sigla: 'MS', nome: 'Mato Grosso do Sul' },
        { sigla: 'MG', nome: 'Minas Gerais' }, { sigla: 'PA', nome: 'Pará' }, { sigla: 'PB', nome: 'Paraíba' },
        { sigla: 'PR', nome: 'Paraná' }, { sigla: 'PE', nome: 'Pernambuco' }, { sigla: 'PI', nome: 'Piauí' },
        { sigla: 'RJ', nome: 'Rio de Janeiro' }, { sigla: 'RN', nome: 'Rio Grande do Norte' }, { sigla: 'RS', nome: 'Rio Grande do Sul' },
        { sigla: 'RO', nome: 'Rondônia' }, { sigla: 'RR', nome: 'Roraima' }, { sigla: 'SC', nome: 'Santa Catarina' },
        { sigla: 'SP', nome: 'São Paulo' }, { sigla: 'SE', nome: 'Sergipe' }, { sigla: 'TO', nome: 'Tocantins' }
    ]

    useEffect(() => {
        if (carregando) return

        if (!isLogado) {
            navigate('/login')
            return
        }

        // Busca dados atualizados do usuário para garantir saldo de créditos real
        const carregarDadosUsuario = async () => {
            try {
                const response = await authService.getUsuarioLogado();
                if (response.usuario) {
                    atualizarUsuario(response.usuario);
                }
            } catch (error) {
                console.error("Erro ao atualizar dados do usuário:", error);
            }
        };

        carregarDadosUsuario();
    }, [isLogado, navigate, carregando])

    const handleInputChange = (e) => {
        const { name, value } = e.target
        
        setForm(prev => {
            const nextForm = { ...prev, [name]: value };
            
            // Se mudou a categoria, reseta a raça para a primeira da nova categoria
            if (name === 'categoria') {
                const novasRacas = racasPorCategoria[value] || ['Outra'];
                nextForm.raca = novasRacas[0];
            }
            
            return nextForm;
        });
    }

    // Upload de Imagem
    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files)
        if (files.length === 0) return

        setUploading(true)
        setErro('')

        try {
            const novasImagens = [...form.imagens].filter(img => img !== '') // Limpar vazios

            for (const file of files) {
                // Validar tamanho com base no tipo de arquivo (Fotos: 10MB, Vídeos: 100MB)
                const isVideoFile = file.type.startsWith('video/');
                const limitMB = isVideoFile ? 100 : 10;
                if (file.size > limitMB * 1024 * 1024) {
                    throw new Error(`O arquivo ${file.name} passou do limite de ${limitMB}MB. Por favor, envie um arquivo menor.`);
                }

                const response = await uploadService.uploadImagem(file)
                novasImagens.push(response.url)
            }

            // Reorganizar para que os vídeos fiquem em primeiro (Capa)
            const isVideo = url => url.match(/\.(mp4|mov|avi|webm)$/i) || url.includes('/video/upload/');
            novasImagens.sort((a, b) => isVideo(b) - isVideo(a));

            // Manter sempre pelo menos 3 slots (ou apenas o que temos)
            // Na verdade, vamos deixar dinâmico agora
            setForm(prev => ({
                ...prev,
                imagens: novasImagens
            }))

        } catch (error) {
            console.error('Erro no upload:', error)
            setErro(error.message || 'Erro ao fazer upload da imagem.')
        } finally {
            setUploading(false)
            // Resetar input
            e.target.value = ''
        }
    }

    const removeImage = (indexToRemove) => {
        setForm(prev => ({
            ...prev,
            imagens: prev.imagens.filter((_, index) => index !== indexToRemove)
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        setErro('')

        try {
            // Validações básicas
            const imagensValidas = form.imagens.filter(img => img.trim() !== '')
            if (imagensValidas.length < 1) {
                throw new Error('É necessário adicionar pelo menos 1 mídia (foto ou vídeo).')
            }

            if (!form.titulo || !form.preco || !form.quantidade || !form.cidade || !form.estado) {
                throw new Error('Preencha todos os campos obrigatórios.')
            }

            const anuncioData = {
                ...form,
                imagens: imagensValidas,
                imagem: imagensValidas[0], // Capa é a primeira imagem
                vendedor_id: usuario.id,
                criadoEm: new Date().toISOString(),
                visualizacoes: 0
            }

            await anunciosService.criar(anuncioData)

            alert('Anúncio criado com sucesso!')
            navigate('/meus-anuncios')

        } catch (error) {
            setErro(error.message)
            window.scrollTo(0, 0)
        } finally {
            setSubmitting(false)
        }
    }


    const assinaturaValida = usuario?.assinatura_ilimitada === true && 
                             usuario?.expiracao_assinatura && 
                             new Date(usuario?.expiracao_assinatura) > new Date();
    const creditos = parseInt(usuario?.creditos_anuncio) || 0;
    const temPermissao = assinaturaValida || creditos >= 1;

    const handleComprarCredito = async (plano) => {
        try {
            const token = localStorage.getItem('token');
            const resp = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/pagamentos/checkout-anuncio`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ plano })
            });
            const data = await resp.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                alert('Erro ao gerar pagamento');
            }
        } catch (e) {
            console.error(e);
            alert('Falha na comunicação com gateway de pagamento');
        }
    };

    if (carregando) {
        return <div className="loading">Carregando...</div>
    }

    if (!isLogado) return null;

    if (!temPermissao) {
        return (
            <div className="criar-anuncio-page">
                <div className="criar-anuncio-container" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>📢</div>
                    <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#fff' }}>Você ainda não tem anúncios disponíveis</h1>
                    <p style={{ color: 'var(--color-text-secondary)', fontSize: '1.1rem', maxWidth: '500px', margin: '0 auto 2.5rem' }}>
                        Para publicar um anúncio no Agroov, você pode comprar um crédito avulso ou assinar o Plano Premium para anúncios ilimitados.
                    </p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', maxWidth: '600px', margin: '0 auto' }}>
                        <div className="plano-card-mini" style={{ background: 'var(--color-bg-card)', padding: '2rem', borderRadius: '16px', border: '1px solid var(--color-border)' }}>
                            <h3 style={{ marginBottom: '0.5rem' }}>Anúncio Avulso</h3>
                            <div style={{ fontSize: '1.5rem', fontWeight: '800', color: 'var(--color-success)', marginBottom: '0.5rem' }}>R$ 49,90</div>
                            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>Validade de 30 dias</p>
                            <button className="btn btn-primary btn-block" onClick={() => handleComprarCredito('anuncio_avulso')}>Comprar 1 Crédito</button>
                        </div>
                        
                        <div className="plano-card-mini" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '2rem', borderRadius: '16px', border: '1px solid var(--color-primary)' }}>
                            <h3 style={{ marginBottom: '0.5rem', color: '#fff' }}>Plano Premium</h3>
                            <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#f59e0b', marginBottom: '0.5rem' }}>R$ 149,90<small style={{fontSize: '0.8rem', opacity: 0.8}}>/mês</small></div>
                            <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>Validade de 60 dias por anúncio</p>
                            <button className="btn btn-secondary btn-block" onClick={() => handleComprarCredito('anuncios_ilimitados')}>Assinar Ilimitado</button>
                        </div>
                    </div>
                    
                    <button className="btn btn-link mt-4" onClick={() => navigate('/')} style={{ color: 'var(--color-text-muted)', marginTop: '2rem' }}>
                        Voltar para a página inicial
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="criar-anuncio-page">
            <div className="criar-anuncio-container">
                <header className="criar-anuncio-header">
                    <h1>Novo Anúncio</h1>
                    <p>{form.categoria === 'Outros' ? 'Preencha os detalhes para vender seu produto' : 'Preencha os detalhes para divulgar seus animais'}</p>
                </header>

                {erro && (
                    <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                        {erro}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* Seção 1: Dados do Animal */}
                    <div className="form-section">
                        <h3>{form.categoria === 'Outros' ? '📦 Detalhes do Produto/Máquina' : '🐮 Detalhes do Animal'}</h3>
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label className="form-label">Título do Anúncio *</label>
                                <input
                                    type="text"
                                    name="titulo"
                                    className="form-input"
                                    placeholder="Ex: Lote de 50 Garrotes Nelore"
                                    value={form.titulo}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Categoria</label>
                                <select name="categoria" className="form-select" value={form.categoria} onChange={handleInputChange}>
                                    <option value="Bovino">Bovino</option>
                                    <option value="Equino">Equino</option>
                                    <option value="Suíno">Suíno</option>
                                    <option value="Ovino">Ovino</option>
                                    <option value="Aves">Aves</option>
                                    <option value="Outros">Outros</option>
                                </select>
                            </div>

                            {form.categoria !== 'Outros' && (
                                <>
                                    <div className="form-group">
                                        <label className="form-label">Raça *</label>
                                        <select name="raca" className="form-select" value={form.raca} onChange={handleInputChange}>
                                            {racasPorCategoria[form.categoria]?.map(raca => (
                                                <option key={raca} value={raca}>{raca}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Gênero</label>
                                        <select name="genero" className="form-select" value={form.genero} onChange={handleInputChange}>
                                            <option value="Macho">Macho</option>
                                            <option value="Fêmea">Fêmea</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className="form-group">
                                <label className="form-label">Quantidade ({form.categoria === 'Bovino' || form.categoria === 'Equino' ? 'Cabeças' : 'Unidades'}) *</label>
                                <input
                                    type="number"
                                    name="quantidade"
                                    className="form-input"
                                    placeholder="Ex: 50"
                                    value={form.quantidade}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            {form.categoria !== 'Outros' && (
                                <>
                            {form.categoria !== 'Outros' && (
                                <div className="form-grid" style={{ gridColumn: 'span 1', display: 'flex', gap: '10px' }}>
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label className="form-label">Peso Médio</label>
                                        <input
                                            type="number"
                                            name="peso"
                                            className="form-input"
                                            placeholder={form.unidadePeso === 'kg' ? "Ex: 450" : "Ex: 15"}
                                            value={form.peso}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group" style={{ width: '100px' }}>
                                        <label className="form-label">Unidade</label>
                                        <select name="unidadePeso" className="form-select" value={form.unidadePeso} onChange={handleInputChange}>
                                            <option value="kg">kg</option>
                                            <option value="@">@ (Arr)</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                                    <div className="form-grid" style={{ gridColumn: 'span 1', display: 'flex', gap: '10px' }}>
                                        <div className="form-group" style={{ flex: 1 }}>
                                            <label className="form-label">Idade</label>
                                            <input
                                                type="number"
                                                name="idade"
                                                className="form-input"
                                                placeholder="Ex: 24"
                                                value={form.idade}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className="form-group" style={{ width: '100px' }}>
                                            <label className="form-label">Unidade</label>
                                            <select name="unidadeIdade" className="form-select" value={form.unidadeIdade} onChange={handleInputChange}>
                                                <option value="meses">Meses</option>
                                                <option value="anos">Anos</option>
                                            </select>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="form-group full-width" style={{ marginTop: '1rem' }}>
                            <label className="form-label">Descrição Completa</label>
                            <textarea
                                name="descricao"
                                className="form-textarea"
                                placeholder="Descreva detalhes importantes sobre o lote..."
                                value={form.descricao}
                                onChange={handleInputChange}
                            ></textarea>
                        </div>
                    </div>

                    {/* Seção 2: Localização e Origem */}
                    <div className="form-section">
                        <h3>📍 Localização e Origem</h3>
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label className="form-label">Nome da Fazenda *</label>
                                <input
                                    type="text"
                                    name="nomeFazenda"
                                    className="form-input"
                                    placeholder="Ex: Fazenda Santa Maria"
                                    value={form.nomeFazenda}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Cidade *</label>
                                <input
                                    type="text"
                                    name="cidade"
                                    className="form-input"
                                    placeholder="Cidade onde estão os animais"
                                    value={form.cidade}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Estado *</label>
                                <select name="estado" className="form-select" value={form.estado} onChange={handleInputChange} required>
                                    <option value="">Selecione</option>
                                    {estados.map(estado => (
                                        <option key={estado.sigla} value={estado.sigla}>{estado.nome}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Seção 3: Valor e Mídia */}
                    <div className="form-section">
                        <h3>💰 Valor e Fotos</h3>
                        <div className="form-grid">
                            <div className="form-group full-width">
                                <label className="form-label">Valor Estimado (R$) *</label>
                                <input
                                    type="number"
                                    name="preco"
                                    className="form-input"
                                    placeholder="Ex: 150000.00"
                                    value={form.preco}
                                    onChange={handleInputChange}
                                    required
                                />
                                <small className="text-muted">
                                    {form.categoria === 'Outros' 
                                        ? 'Valor total do lote ou item (especifique na descrição)' 
                                        : 'Valor total do lote ou por cabeça (especifique na descrição)'}
                                </small>
                            </div>

                            <div className="form-group full-width">
                                <div className="form-group full-width">
                                    <label className="form-label">
                                        Fotos e Vídeos do {form.categoria === 'Outros' ? 'Item' : 'Animal'} (Mínimo 1, max 50MB) *
                                    </label>

                                    <div className="upload-container">
                                        <input
                                            type="file"
                                            id="file-upload"
                                            multiple
                                            accept="image/*,video/mp4,video/mov,video/avi,video/webm"
                                            onChange={handleFileUpload}
                                            style={{ display: 'none' }}
                                            disabled={uploading}
                                        />
                                        <label htmlFor="file-upload" className={`btn btn-outline btn-block ${uploading ? 'disabled' : ''}`} style={{ cursor: 'pointer', textAlign: 'center' }}>
                                            {uploading ? '⏳ Enviando arquivos...' : '📷 Escolher Fotos/Vídeos'}
                                        </label>
                                    </div>

                                    <div className="image-preview-container" style={{ marginTop: '1.5rem' }}>
                                        {form.imagens.length === 0 && (
                                            <p className="text-muted" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
                                                Nenhuma foto selecionada.
                                            </p>
                                        )}

                                        {form.imagens.map((img, index) => {
                                            if (!img) return null;
                                            const isVideo = img.match(/\.(mp4|mov|avi|webm)$/i) || img.includes('/video/upload/');
                                            return (
                                                <div key={index} className="image-preview-item">
                                                    {isVideo ? (
                                                        <video src={img} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <img src={img} alt={`Mídia ${index + 1}`} />
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn-remove-image"
                                                        onClick={() => removeImage(index)}
                                                        title="Remover foto"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            )
                                        })}
                                    </div>
                                    <small className="text-muted">Formatos: JPG, PNG, MP4, MOV. Máx: 50MB.</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="alert-warning" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', padding: '1rem', borderRadius: '12px', marginBottom: '2rem', border: '1px solid rgba(245, 158, 11, 0.3)', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <span style={{ fontSize: '1.5rem', lineHeight: '1' }}>⚠️</span>
                        <div>
                            <strong style={{ display: 'block', marginBottom: '4px' }}>Atenção: Regra de Segurança</strong>
                            <p style={{ margin: 0, fontSize: '0.95rem', opacity: 0.9 }}>Por medidas de segurança da plataforma, você só poderá alterar os dados deste anúncio nas <strong>primeiras 24 horas</strong> após a publicação. Verifique todas as fotos e informações antes de continuar.</p>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary btn-block" onClick={() => navigate('/meus-anuncios')}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
                            {submitting ? 'Publicando...' : 'Publicar Anúncio'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default CriarAnuncio
